import { useCallback, useEffect, useState } from "react";

import ExpandIcon from "@hl/base-components/lib/assets/icons.generated/Expand";
import LinkExternal01 from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/General/LinkExternal01";
import ImageIcon from "@hl/base-components/lib/assets/icons.generated/Image";
import MinimizeIcon from "@hl/base-components/lib/assets/icons.generated/Minimize";
import RefreshIcon from "@hl/base-components/lib/assets/icons.generated/PlayFilled";
import FullScreenIcon from "@hl/base-components/lib/assets/icons.generated/Screen";
import ShuffleIcon from "@hl/base-components/lib/assets/icons.generated/Shuffle";
import { GenArtUrlIFrame } from "@hl/shared-features/lib/features/gen-art/GenArtIFrame";
import {
  ArtworkInteractionType,
  trackArtworkInteraction,
} from "@hl/shared-features/lib/features/gen-art/tracking";
import { useCollectionTokensV2Query } from "@hl/shared-features/lib/features/marketplace/queries/marketplace.graphql.generated";
import useGenArtAspectRatio from "@hl/shared-features/lib/hooks/useAspectRatio";
import {
  ActionIcon,
  Box,
  Flex,
  Group,
  Modal,
  Tooltip,
  createStyles,
  Center,
} from "@mantine/core";
import { useDisclosure, useFullscreen } from "@mantine/hooks";

import useMintState from "~hooks/useMintState";

import { CollectionContractType } from "../../apollo/graphql.generated";

import { GetSalePageCollectionQuery } from "./queries.graphql.generated";

type StyleProps = {
  expanded: boolean;
  responsive: boolean;
  isApollo?: boolean;
};

const useStyles = createStyles(
  (theme, { expanded, responsive, isApollo }: StyleProps) => {
    const heightPadding = expanded ? 30 : 408;
    return {
      containerResponsive: {
        width: "calc(100% - 120px)",
        height: "75vh",
        minHeight: 200,
        maxHeight: 1200,

        [theme.fn.smallerThan(600)]: {
          width: "calc(100% - 32px)",
          height: "66.67vh",
        },

        ...(expanded && {
          width: "100%",
          height: "100%",

          [theme.fn.smallerThan(600)]: {
            width: "100%",
            height: "100vh",
          },
        }),
      },
      embedContainer: {
        width: "100%",
      },
      container: {
        width: "100%",
        maxWidth: "100%",
        boxShadow: theme.shadows.sm,
        [theme.fn.largerThan("md")]: {
          width: "auto",
          height: `calc(100vh - ${heightPadding}px)`,
          minHeight: isApollo ? undefined : 720,
          ["@media (orientation: portrait)"]: {
            width: "100%",
            height: "auto",
            maxHeight: `calc(100vh - ${heightPadding}px)`,
          },
        },
      },
      absolutePosition: {
        position: "absolute",
        top: 0,
      },
      previewImage: {
        objectFit: "scale-down",
      },
      expanded: {
        height: responsive ? "100vh" : "calc(100vh - 30px)",
        overflow: "hidden",
      },
      closeBtn: {
        position: "absolute",
        top: 10,
        right: 10,
      },
      fullscreenButton: {
        [theme.fn.smallerThan("sm")]: {
          display: "none",
        },
      },
      toolbar: {
        boxShadow: theme.shadows.sm,
        borderRadius: 10,
        backgroundColor: theme.colors.primaryBackground[0],
        ...(expanded && {
          position: "absolute",
          bottom: 20,
        }),
        button: {
          height: 36,
          width: 36,
        },
        ...(expanded &&
          responsive && {
            display: "none",
          }),
      },
      modal: {
        ...(responsive && {
          padding: `0px !important`,
        }),
      },
      modalBody: {
        maxHeight: "100%",
        ...(responsive && {
          padding: 0,
          margin: 0,
        }),
      },
    };
  }
);

type Props = {
  collection: GetSalePageCollectionQuery["getPublicCollection"];
  isEmbed?: boolean;
  isApollo?: boolean;
};

export const GenArtHeaderImported = ({
  collection,
  isEmbed = false,
  isApollo = false,
}: Props) => {
  const { address, chainId, marketplaceId } = useMintState();
  const [opened, { open, close: closeModal }] = useDisclosure(false);
  const { classes } = useStyles({
    expanded: opened,
    responsive: false,
    isApollo,
  });
  const { ref: iframeRef, toggle: toggleFullscreen } = useFullscreen();
  const [isImagePreview, setImagePreview] = useState(false);
  const iconSize = { width: 16, height: 16 };
  const [refreshKey, setRefreshKey] = useState(0);
  const isImported = collection.type === CollectionContractType.Imported;

  const showImage = useCallback(() => {
    setImagePreview(true);
    open();
  }, [open]);
  const close = useCallback(() => {
    closeModal();
    setImagePreview(false);
  }, [closeModal]);

  const queryParams = {
    forceUserBalanceAddress: null,
    collectionMarketplaceId: marketplaceId,
    ownerAddress: null,
    tokenAddress: address,
    after: null,
    first: 1,
    tokenNameOrId: null,
    sortBy: null,
    sortDirection: null,
    minted: null,
    attributes: null,
    chainId: chainId,
    editionId: (collection.entityId ?? collection.editionId ?? "0").toString(),
    withListings: null,
    withOffers: null,
    isImportedCollection: collection.type === CollectionContractType.Imported,
  };

  const { data: tokensData, refetch: refetch } = useCollectionTokensV2Query({
    variables: queryParams,
    fetchPolicy: "network-only",
  });
  const response = tokensData?.getCollectionTokensV2;
  const token = response?.edges[0];
  const endCursor = response?.pageInfo.endCursor;
  const mediaUrl = token?.animationUrl
    ? token.animationUrl + (isImported ? "" : `?refreshKey=${refreshKey}`)
    : "";

  const { aspectRatio } = useGenArtAspectRatio(null, token?.imageUrl);

  useEffect(() => {
    setRefreshKey((rk) => rk + 1);
  }, [aspectRatio]);

  const randomize = useCallback(() => {
    refetch({
      ...queryParams,
      after: endCursor,
      first: 1,
    });
  }, [endCursor]);

  const trackUsage = useCallback(
    (action: ArtworkInteractionType) => {
      trackArtworkInteraction({
        collectionId: collection.id,
        interactionType: action,
      });
    },
    [collection]
  );

  const content = (
    <Flex
      direction="column"
      align="center"
      justify="center"
      w={"100%"}
      h={opened ? "100vh" : "auto"}
    >
      <Box
        className={
          isEmbed && !opened ? classes.embedContainer : classes.container
        }
        sx={{ aspectRatio, position: "relative" }}
      >
        <GenArtUrlIFrame
          enableRestrictiveFlags={
            collection.flagVariations.enableIframeRestrictiveFlags
          }
          allowIframeSameOrigin={
            collection.flagVariations.allowIframeSameOrigin
          }
          url={mediaUrl}
          extraClass={classes.absolutePosition}
          bordered={false}
          aspectRatio={aspectRatio}
          iframeRef={iframeRef}
        />
      </Box>
      <Box
        p={4}
        mt={{ base: 16, md: isEmbed ? 16 : 32 }}
        className={classes.toolbar}
      >
        <Group spacing={4}>
          <Tooltip label="Reload">
            <ActionIcon
              onClick={() => {
                setRefreshKey(refreshKey + 1);
                trackUsage(ArtworkInteractionType.Reload);
              }}
            >
              <RefreshIcon {...iconSize} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label="Randomize">
            <ActionIcon
              onClick={() => {
                randomize();
                trackUsage(ArtworkInteractionType.Randomize);
              }}
            >
              <ShuffleIcon {...iconSize} />
            </ActionIcon>
          </Tooltip>
          {!opened && (
            <Tooltip label="Expand">
              <ActionIcon
                onClick={() => {
                  open();
                  trackUsage(ArtworkInteractionType.Expand);
                }}
              >
                <ExpandIcon {...iconSize} />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip label="Fullscreen" className={classes.fullscreenButton}>
            <ActionIcon
              onClick={() => {
                toggleFullscreen();
                trackUsage(ArtworkInteractionType.Fullscreen);
              }}
            >
              <FullScreenIcon {...iconSize} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Preview image">
            <ActionIcon
              onClick={() => {
                showImage();
                trackUsage(ArtworkInteractionType.PreviewImage);
              }}
            >
              <ImageIcon {...iconSize} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Open in a new window">
            <ActionIcon
              onClick={() => {
                if (!mediaUrl) return;
                window.open(mediaUrl, "_blank", "noreferrer");
                trackUsage(ArtworkInteractionType.OpenInNewWindow);
              }}
            >
              <LinkExternal01 {...iconSize} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Box>
    </Flex>
  );
  if (opened) {
    return (
      <Modal
        opened={opened}
        onClose={close}
        fullScreen
        withCloseButton={false}
        centered
        classNames={{
          modal: classes.modal,
          body: classes.modalBody,
        }}
      >
        <div className={classes.expanded}>
          {isImagePreview ? (
            <PreviewImage imageUrl={token?.imageUrl} />
          ) : (
            content
          )}
        </div>
        <ActionIcon className={classes.closeBtn} onClick={close}>
          <MinimizeIcon />
        </ActionIcon>
      </Modal>
    );
  }

  return content;
};

type PreviewProps = {
  imageUrl?: string;
};

const PreviewImage = ({ imageUrl }: PreviewProps) => {
  const { classes, cx } = useStyles({ expanded: true, responsive: false });
  return imageUrl ? (
    <Flex justify="center" align="center" pos="relative" h="100%">
      <img
        className={cx([classes.container, classes.previewImage])}
        src={imageUrl}
      />
    </Flex>
  ) : (
    <Center>Failed to render</Center>
  );
};
